import React, { useState, useEffect, useCallback } from 'react';

const TIZEN_LOG = process.env.REACT_APP_TIZEN_ONSCREEN_LOG === 'true';
const HTTP_LOG = process.env.REACT_APP_HTTP_STREAM_LOG === 'true';
const ENABLED = TIZEN_LOG || HTTP_LOG;

/**
 * On-screen debug overlay for Tizen TVs.
 * REACT_APP_TIZEN_ONSCREEN_LOG=true — all tizenLog lines
 * REACT_APP_HTTP_STREAM_LOG=true — HTTP(S) channel logs only (lighter)
 *
 * Shows a semi-transparent panel in the top-left corner with the last 30
 * tizenLog() lines. Tap/click the panel to toggle collapse.
 */
function TizenDebugOverlay() {
  const [lines, setLines] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  const refresh = useCallback(() => {
    setLines([...(window.__tizenLogLines || [])]);
  }, []);

  useEffect(() => {
    if (!ENABLED) return;
    // Register the refresh hook so tizenLog() can trigger re-renders
    window.__tizenLogRefresh = refresh;
    // Pull any lines that were logged before this component mounted
    refresh();
    return () => {
      window.__tizenLogRefresh = null;
    };
  }, [refresh]);

  if (!ENABLED) return null;

  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 99999,
    maxWidth: collapsed ? '120px' : '780px',
    maxHeight: collapsed ? '36px' : '420px',
    background: 'rgba(0,0,0,0.82)',
    color: '#e0e0e0',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.45',
    borderRight: '2px solid #555',
    borderBottom: '2px solid #555',
    overflow: 'hidden',
    userSelect: 'none',
    cursor: 'pointer',
    padding: collapsed ? '6px 10px' : '0',
  };

  const headerStyle = {
    background: '#1a1a2e',
    color: '#7ecfff',
    padding: '5px 10px',
    fontWeight: 'bold',
    fontSize: '13px',
    borderBottom: '1px solid #444',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const bodyStyle = {
    overflowY: 'auto',
    maxHeight: '380px',
    padding: '6px 10px',
  };

  const lineColor = (l) => {
    if (l.includes('ERROR')) return '#ff6b6b';
    if (l.includes('WARN'))  return '#ffd93d';
    return '#e0e0e0';
  };

  if (collapsed) {
    return (
      <div style={containerStyle} onClick={() => setCollapsed(false)}>
        📺 DEBUG [{lines.length}]
      </div>
    );
  }

  return (
    <div style={containerStyle} onClick={() => setCollapsed(true)}>
      <div style={headerStyle}>
        <span>
          📺 {HTTP_LOG && !TIZEN_LOG ? 'HTTP Stream Debug' : 'Tizen Debug'} — tap to collapse
        </span>
        <span style={{ color: '#aaa', fontSize: '11px' }}>{lines.length} lines</span>
      </div>
      <div style={bodyStyle} onClick={(e) => e.stopPropagation()}>
        {lines.length === 0 && (
          <div style={{ color: '#888', fontStyle: 'italic' }}>No logs yet…</div>
        )}
        {lines.map((l, i) => (
          <div key={i} style={{ color: lineColor(l), marginBottom: '1px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {l}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TizenDebugOverlay;
